import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
    CreateWorkflowData,
    Field,
    JoiRule,
    PatchWorkflowData,
    Product,
    WorkflowConfig,
    WorkflowConfigField,
    WorkflowConfigFieldInputType,
    WorkflowConfigFieldValidationRule,
    WorkflowConfigSection,
    WorkflowConfigStep,
    WorkflowData
} from '@monorepo/interfaces';
import { WorkflowOperationType } from '../enums/WorkflowOperationType.enum';
import { WorkflowProcessorFactory } from '../utils/workflow-factory/WorkflowProcessorFactory';
import { PatchWorkflowProcessor } from '../utils/workflow-factory/PatchWorkflowProcessor';
import { WorkflowFormTransformerOperations } from '../utils/workflow-factory/WorkflowFormTransformerOperations';
import { createDateGreaterThanValidator } from '../utils/date-now-validator';

@Injectable({
    providedIn: 'root'
})
export class WorkflowFormService {
    private readonly formBuilder = inject(FormBuilder);

    //****************************(FORM INITIALIZATION) ******************************************
    // **************** WORKFLOW FORM STEPS/SUBSTEPS INITIALIZATION  ************************
    //************************************************** *******************************************

    /**
     * Creates a nested form structure based on the workflow configuration
     * @description Main method to create the form
     * @param config {WorkflowConfig} The workflow configuration
     * @param workflowData {WorkflowData} The initial data for the workflow, if any
     * @returns FormGroup representing the entire workflow
     */
    public createWorkflowForm(config: WorkflowConfig, workflowData?: WorkflowData): FormGroup {
        const workflowForm = this.formBuilder.group({});
        // Add workflow-level fields
        this.addFieldsToForm(config.fields || [], workflowForm, workflowData);
        // Add steps if they exist
        this.initializeStepsFormGroups(config, workflowForm, workflowData);
        // Add products with config for validators
        this.initializeProductsFormGroups(config, workflowForm, workflowData);
        return workflowForm;
    }

    /**
     * Creates a form group for a step or section
     * @description This RECURSIVE method initializes a form group for a given step, including its fields and sections.
     * @param step {WorkflowConfigStep | WorkflowConfigSection}The step configuration
     * @param workflowData {WorkflowData} The initial data for the workflow, if any
     * @returns FormGroup for the step
     */
    private createStepFormGroup(
        step: WorkflowConfigStep | WorkflowConfigSection,
        workflowData?: WorkflowData
    ): FormGroup {
        const stepGroup = this.formBuilder.group({});
        // Add fields to the step
        this.addFieldsToForm(step.fields || [], stepGroup, workflowData);
        // Process sections  if they exist recursively
        this.initializeSectionsFormGroup(step, stepGroup, workflowData);
        return stepGroup;
    }

    /**
     * Creates a form group for a product
     * @description This method initializes a form group for a given product, including its fields.
     * @param product {Product} The product data
     * @param config {WorkflowConfig} The workflow configuration containing field definitions with validators
     * @returns FormGroup for the product
     */
    private createProductFormGroup(product: Product, config: WorkflowConfig): FormGroup {
        const productGroup = this.formBuilder.group({});
        this.addFieldsToProductForm(product.fields || [], productGroup, config);
        return productGroup;
    }

    /**
     * Initializes form groups for each step in the workflow
     * @description This method creates a form group for each step in the workflow configuration and adds it to the main form.
     * @param config {WorkflowConfig} The workflow configuration
     * @param workflowData {WorkflowData} The initial data for the workflow, if any
     * @param workflowForm {FormGroup} The main form group for the workflow
     */
    private initializeStepsFormGroups(
        config: WorkflowConfig,
        workflowForm: FormGroup,
        workflowData?: WorkflowData
    ): void {
        if (config.steps?.length) {
            const stepsGroup = this.formBuilder.group({});
            // If we have data, we show all steps, otherwise we only show those marked for creation, since we are in the creation phase
            config.steps
                // .filter(step => workflowData || step.shownInCreate)
                .forEach(step =>
                    stepsGroup.addControl(`step-${step.name}`, this.createStepFormGroup(step, workflowData))
                );
            workflowForm.addControl('steps', stepsGroup);
        }
    }

    /**
     * Initializes form groups for each product in the workflow
     * @description This method creates a form group for each product in the workflow data and adds it to the main form.
     * @param config {WorkflowConfig} The workflow configuration containing field definitions
     * @param workflowForm {FormGroup} The main form group for the workflow
     * @param workflowData {WorkflowData} The initial data for the workflow, if any
     */
    private initializeProductsFormGroups(
        config: WorkflowConfig,
        workflowForm: FormGroup,
        workflowData?: WorkflowData
    ): void {
        if (workflowData?.products?.length) {
            const productsGroup = this.formBuilder.group({});
            workflowData.products.forEach(product =>
                productsGroup.addControl(`product-${product.name}`, this.createProductFormGroup(product, config))
            );
            workflowForm.addControl('products', productsGroup);
        }
    }

    /**
     * Initializes sections form groups within a step
     * @description This method creates a form group for each section within a step and adds it to the step's form group.
     * @param step {WorkflowConfigStep} The step configuration
     * @param stepGroup {FormGroup} The form group for the step
     * @param workflowData {WorkflowData} The initial data for the workflow, if any
     * @returns void
     */
    private initializeSectionsFormGroup(
        step: WorkflowConfigStep | WorkflowConfigSection,
        stepGroup: FormGroup,
        workflowData?: WorkflowData
    ): void {
        if ('sections' in step && step.sections?.length) {
            const sectionsFormGroup = this.formBuilder.group({});

            // If we have data, we show all sections, otherwise we only show those marked for creation, since we are in the creation phase
            step.sections
                // .filter(section => workflowData || section.shownInCreate || section.staticTab)
                .forEach(section => {
                    sectionsFormGroup.addControl(
                        `section-${section.name}`,
                        this.createStepFormGroup(section, workflowData)
                    );
                });

            stepGroup.addControl('sections', sectionsFormGroup);
        }
    }

    /**
     * Adds a collection of fields to a form group
     * @description This method iterates over an array of field configurations and adds each one to the provided form group.
     * @param fields {WorkflowConfigField[]} Array of field configurations
     * @param form {FormGroup} The form group to add controls to
     * @param workflowData {WorkflowData } Optional initial data for the workflow, used to pre-populate fields
     */
    private addFieldsToForm(fields: WorkflowConfigField[], form: FormGroup, workflowData?: WorkflowData): void {
        fields.forEach(field => {
            // disable (date > now)-validators for startDate and EndDate if workflow already exists
            if (['start-date', 'end-date'].includes(field.name) && workflowData) {
                field.validationRules = [];
            }
            form.addControl(field.name, this.createFormControl(field, this.findFieldValue(field, workflowData)));
        });
    }

    /**
     * Adds a collection of fields to a product form group
     * @description This method iterates over an array of field configurations and adds each one to the provided form group.
     * It finds the corresponding WorkflowConfigField to apply the same validators as the main field.
     * @param fields {Field[]} Array of field data from the product
     * @param form {FormGroup} The form group to add controls to
     * @param config {WorkflowConfig} The workflow configuration containing field definitions with validators
     */
    private addFieldsToProductForm(fields: Field[], form: FormGroup, config: WorkflowConfig): void {
        fields.forEach(field => {
            // Find the corresponding WorkflowConfigField to get validators
            const configField = this.findProductRelevantFieldConfig(field.name, config);

            if (configField) {
                // Use the same createFormControl method to apply validators
                const control = this.createFormControl(configField, field.value);
                form.addControl(field.name, control);
            } else {
                // Fallback: create control without validators if config field not found
                form.addControl(field.name, new FormControl(field.value));
            }
        });
    }

    /**
     * Finds a product-relevant WorkflowConfigField by name across all steps and sections
     * @description Searches through the workflow configuration to find the field definition
     * @param fieldName {string} The name of the field to find
     * @param config {WorkflowConfig} The workflow configuration to search
     * @returns The WorkflowConfigField if found, null otherwise
     */
    private findProductRelevantFieldConfig(fieldName: string, config: WorkflowConfig): WorkflowConfigField | null {
        // Search in top-level fields
        const topLevelField = config.fields?.find(f => f.name === fieldName && f.productRelevant);
        if (topLevelField) return topLevelField;

        // Search in steps
        if (config.steps) {
            for (const step of config.steps) {
                // Search in step fields
                const stepField = step.fields?.find(f => f.name === fieldName && f.productRelevant);
                if (stepField) return stepField;

                // Search in section fields
                if (step.sections) {
                    for (const section of step.sections) {
                        const sectionField = section.fields?.find(f => f.name === fieldName && f.productRelevant);
                        if (sectionField) return sectionField;
                    }
                }
            }
        }

        return null;
    }

    /**
     * Creates a form control based on field configuration and pre-populated value if any
     * @description This method creates a form control with the appropriate validators, prepopulated data, based on the field configuration.
     * @param field {WorkflowConfigField} The field configuration
     * @param fieldValue {string | string[] | boolean} The initial value for the field, if any
     * @returns FormControl configured according to the field specs
     */
    private createFormControl(field: WorkflowConfigField, fieldValue: string | string[] | boolean): FormControl {
        const validators = this.buildValidators(field);
        return new FormControl({ value: fieldValue, disabled: field.disabled }, validators);
    }

    /**
     * Builds an array of Angular validators based on the field configuration
     * @description Maps Joi validation rules to Angular validators, considering the field's type and validation requirements
     * @param field {WorkflowConfigField} The field configuration containing validation rules
     * @returns Array of Angular validator functions
     */
    private buildValidators(field: WorkflowConfigField): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        // Add required validator if field is required
        if (field.required) {
            validators.push(Validators.required);
        }

        // Process custom validation rules if they exist
        if (field.validationRules?.length) {
            field.validationRules.forEach(rule => {
                const validator = this.mapJoiRuleToAngularValidator(rule, field.typeForValidation);
                if (validator) {
                    validators.push(validator);
                }
            });
        }

        return validators;
    }

    /**
     * Maps a Joi validation rule to the corresponding Angular validator
     * @description Converts Joi rules (from the JoiRule enum) to Angular validator functions, considering the field type
     * @param rule {WorkflowConfigFieldValidationRule} The Joi validation rule configuration
     * @param fieldType {WorkflowConfigFieldInputType} The type of the field for context-specific validation
     * @returns Angular validator function or null if no mapping exists
     */
    private mapJoiRuleToAngularValidator(
        rule: WorkflowConfigFieldValidationRule,
        fieldType?: WorkflowConfigFieldInputType
    ): ValidatorFn | null {
        switch (rule.rule) {
            case JoiRule.MIN:
                switch (fieldType) {
                    case WorkflowConfigFieldInputType.DECIMAL:
                    case WorkflowConfigFieldInputType.FLOAT:
                    case WorkflowConfigFieldInputType.NUMBER:
                        return Validators.min(Number(rule.value));
                    case WorkflowConfigFieldInputType.STRING:
                    case WorkflowConfigFieldInputType.TEXT:
                        return Validators.minLength(Number(rule.value));
                    default:
                        return null;
                }

            case JoiRule.MAX:
                switch (fieldType) {
                    case WorkflowConfigFieldInputType.DECIMAL:
                    case WorkflowConfigFieldInputType.FLOAT:
                    case WorkflowConfigFieldInputType.NUMBER:
                        return Validators.max(Number(rule.value));
                    case WorkflowConfigFieldInputType.STRING:
                    case WorkflowConfigFieldInputType.TEXT:
                        return Validators.maxLength(Number(rule.value));
                    default:
                        return null;
                }

            case JoiRule.GREATER:
                switch (fieldType) {
                    case WorkflowConfigFieldInputType.DECIMAL:
                    case WorkflowConfigFieldInputType.FLOAT:
                    case WorkflowConfigFieldInputType.NUMBER:
                        return Validators.min(Number(rule.value) + 1);
                    case WorkflowConfigFieldInputType.DATE:
                        return createDateGreaterThanValidator(rule.value as string);
                    default:
                        return null;
                }

            case JoiRule.LESS:
                switch (fieldType) {
                    case WorkflowConfigFieldInputType.DECIMAL:
                    case WorkflowConfigFieldInputType.FLOAT:
                    case WorkflowConfigFieldInputType.NUMBER:
                        return Validators.max(Number(rule.value) - 1);
                    default:
                        return null;
                }

            case JoiRule.INTEGER:
                switch (fieldType) {
                    case WorkflowConfigFieldInputType.NUMBER:
                        return Validators.pattern('^[0-9]+$');
                    default:
                        return null;
                }

            case JoiRule.POSITIVE:
                switch (fieldType) {
                    case WorkflowConfigFieldInputType.DECIMAL:
                    case WorkflowConfigFieldInputType.FLOAT:
                    case WorkflowConfigFieldInputType.NUMBER:
                        return Validators.min(1);
                    default:
                        return null;
                }

            case JoiRule.NEGATIVE:
                switch (fieldType) {
                    case WorkflowConfigFieldInputType.DECIMAL:
                    case WorkflowConfigFieldInputType.FLOAT:
                    case WorkflowConfigFieldInputType.NUMBER:
                        return Validators.max(-1);
                    default:
                        return null;
                }

            case JoiRule.ISO:
                switch (fieldType) {
                    case WorkflowConfigFieldInputType.DATE:
                        // ISO date format validation (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss.sssZ)
                        return Validators.pattern('^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d{3})?Z?)?$');
                    default:
                        return null;
                }

            case JoiRule.PATTERN:
                return Validators.pattern(String(rule.value));

            case JoiRule.ALPHAPNUM:
                return Validators.pattern('^[a-zA-Z0-9]+$');

            case JoiRule.GUID:
                return Validators.pattern('^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$');

            default:
                console.warn(`Unsupported Joi rule: ${rule.rule}`);
                return null;
        }
    }

    //********************************(POPULATE DATA) ******************************************
    // ************************ WORKFLOW CONTROLS VALUES ***********************************
    //************************************************** ******************************************

    /**
     * Retrieves the value for a workflow field, handling both general and product-relevant fields.
     *
     * @description
     * If the field is marked as product-relevant, delegates to a helper to accumulate all related product field values.
     * Otherwise, finds the field in the general workflow data and returns its value.
     *
     * @param workflowFieldConfig {WorkflowConfigField} The configuration object for the field.
     * @param workflowData {WorkflowData} The workflow data containing field values and products.
     * @returns {string | string[] | boolean | null} The field value as a string, string array (for multi-select), or null if not found.
     */
    private findFieldValue(
        workflowFieldConfig: WorkflowConfigField,
        workflowData: WorkflowData
    ): string | string[] | boolean {
        if (!workflowData || !workflowData.fields || !workflowData.products) {
            return null;
        }

        let fieldValue: string;
        if (workflowFieldConfig.productRelevant) {
            fieldValue = this.getProductRelevantFieldValue(workflowData, workflowFieldConfig);
        } else {
            const field = workflowData.fields.find(field => field.name === workflowFieldConfig.name);
            fieldValue = field ? field.value : null;
        }
        return WorkflowFormTransformerOperations.getFieldValueStringOrStringArray(workflowFieldConfig, fieldValue);
    }

    /**
     * Accumulates all values for a product-relevant field into a comma-separated string.
     *
     * @description
     * Finds the product by name and reduces its fields array, concatenating each field's value.
     * Handles undefined values gracefully and returns null if the product or its fields are missing.
     *
     * @param workflowData {WorkflowData} The workflow data containing products.
     * @param workflowFieldConfig {WorkflowConfigField} The configuration object for the product-relevant field.
     * @returns A comma-separated string of all field values, or null if not found.
     */
    private getProductRelevantFieldValue(workflowData: WorkflowData, workflowFieldConfig: WorkflowConfigField): string {
        const product = workflowData.products[0];
        if (product && product.fields) {
            const existingValue = product.fields.find(field => field.name === workflowFieldConfig.name)?.value;
            if (existingValue !== undefined && existingValue !== null) {
                return existingValue;
            }
        }
        if (workflowFieldConfig.default) return workflowFieldConfig.default;
        return null;
    }

    //********************************(SAVE/UPDATE) ******************************************
    // ************************ WORKFLOW DETAILS FORM PROCESSING  ************************
    //************************************************** ****************************************

    /**
     * Processes the workflow form data for creation or update operations
     *
     * @description This method serves as the entry point for workflow form processing,
     * creating the appropriate processor based on the operation type and orchestrating
     * the transformation of form data into the proper structure for API submission.
     * For update operations, it ensures the existing data is provided to the processor
     * for ID preservation and proper product handling.
     *
     * @param operationType {WorkflowOperationType} - Determines whether to process the form for create or update
     * @param config {WorkflowConfig} - The workflow configuration containing structure definition
     * @param formGroup {FormGroup} - The Angular form group containing all form values
     * @param existingData {WorkflowData} - Required for update operations to preserve IDs and handle products correctly
     * @returns A structured object ready for submission to create or update a workflow
     */
    public processWorkflowForm(
        operationType: WorkflowOperationType,
        config: WorkflowConfig,
        formGroup: FormGroup,
        existingData?: WorkflowData
    ): CreateWorkflowData | PatchWorkflowData {
        const processor = WorkflowProcessorFactory.createProcessor(operationType);

        if (operationType === WorkflowOperationType.UPDATE && existingData) {
            (processor as PatchWorkflowProcessor).setExistingWorkflowData(existingData);
        }

        return processor.processWorkflowDetailsForm(config, formGroup);
    }

    /**
     * Validates the workflow form based on the operation type and visible steps
     *
     * @param operationType {WorkflowOperationType} - The type of workflow operation (create, edit, etc.)
     * @param formGroup {FormGroup} - The main form group containing all workflow data
     * @param filteredWorkflowConfigSteps {WorkflowConfigStep[]} - Array of workflow steps that are currently visible/accessible
     * @returns {boolean} - True if all visible steps are valid for the given operation type, false otherwise
     */
    public validateWorkflowForm(
        operationType: WorkflowOperationType,
        formGroup: FormGroup,
        filteredWorkflowConfigSteps: WorkflowConfigStep[]
    ): boolean {
        const processor = WorkflowProcessorFactory.createProcessor(operationType);
        return processor.validateWorkflowForm(formGroup, filteredWorkflowConfigSteps);
    }

    /**
     * Finds all error locations in the workflow form, returning the step and section indices of any
     * invalid controls.
     *
     * @description
     * Walks the nested form structure (`steps → sections`) and collects the index pairs of any
     * section that either has direct errors or contains nested controls with errors.
     * Additionally checks the `products` form group (if present) and maps any invalid,
     * touched product fields back to their corresponding step/section coordinates using the
     * workflow config.
     *
     * The returned indices are used to
     * - scroll the stepper to the first offending step/section, and
     * - highlight invalid stepper steps visually.
     *
     * @param formGroup {FormGroup} - The root workflow form group produced by `createWorkflowForm`.
     * @param config {WorkflowConfig} - Optional workflow configuration needed for mapping product
     *   field errors to their step/section location. If omitted, product errors are skipped.
     * @returns An array of `{ stepIndex, sectionIndex }` objects identifying every step/section
     *   that contains at least one invalid control. May contain duplicate-free section entries;
     *   product errors are de-duplicated per location.
     */
    public findErrorLocations(
        formGroup: FormGroup,
        config?: WorkflowConfig
    ): { stepIndex: number; sectionIndex: number }[] {
        const errorLocations: { stepIndex: number; sectionIndex: number }[] = [];
        const stepsControl = formGroup.get('steps') as FormGroup;
        const productsGroup = formGroup.get('products') as FormGroup;

        // Find errors in steps and sections
        Object.keys(stepsControl.controls).forEach((stepKey, stepIndex) => {
            const stepControl = stepsControl.get(stepKey) as FormGroup;
            const sectionsControl = stepControl.get('sections') as FormGroup;

            if (sectionsControl) {
                Object.keys(sectionsControl.controls).forEach((sectionKey, sectionIndex) => {
                    const sectionControl = sectionsControl.get(sectionKey) as FormGroup;

                    if (sectionControl.errors || this.hasNestedErrors(sectionControl)) {
                        errorLocations.push({
                            stepIndex,
                            sectionIndex
                        });
                    }
                });
            }
        });

        // Find errors in products and map them to their step/section
        if (productsGroup && config) {
            const productErrors = this.findProductFieldErrors(productsGroup, config);
            errorLocations.push(...productErrors);
        }

        return errorLocations;
    }

    /**
     * Returns the invalid field labels and their Angular error keys for a single step.
     *
     * @param formGroup {FormGroup} - The root workflow form group.
     * @param config {WorkflowConfig} - The workflow configuration used to resolve field labels.
     * @param stepIndex {number} - Index of the step to inspect within `config.steps`.
     * @returns Array of `{ label, errorKeys }` — one entry per unique invalid field in that step.
     *   `label` is the raw i18n key; `errorKeys` are the Angular validator error keys (e.g. `['required']`).
     */
    public findInvalidFieldLabels(
        formGroup: FormGroup,
        config?: WorkflowConfig,
        stepIndex = 0
    ): { label: string; errors: Record<string, unknown> }[] {
        if (!config) return [];

        const results: { label: string; errors: Record<string, unknown> }[] = [];
        const stepsControl = formGroup.get('steps') as FormGroup;
        if (!stepsControl) return results;

        const configStep = config.steps?.[stepIndex];
        if (!configStep) return results;

        const stepControl = stepsControl.get(`step-${configStep.name}`) as FormGroup;
        const sectionsControl = stepControl?.get('sections') as FormGroup;
        if (!sectionsControl) return results;

        configStep.sections?.forEach(configSection => {
            const sectionControl = sectionsControl.get(`section-${configSection.name}`) as FormGroup;
            if (!sectionControl) return;

            configSection.fields?.forEach(configField => {
                const fieldControl = sectionControl.get(configField.name);
                if (fieldControl?.invalid) {
                    results.push({ label: configField.label, errors: fieldControl.errors ?? {} });
                }
            });
        });

        return results;
    }

    /**
     * Finds errors in product fields and maps them to their corresponding step/section
     *
     * @param productsGroup {FormGroup} - The form group containing all products
     * @param config {WorkflowConfig} - The workflow configuration to find field locations
     * @returns Array of error locations with stepIndex and sectionIndex
     */
    private findProductFieldErrors(
        productsGroup: FormGroup,
        config: WorkflowConfig
    ): { stepIndex: number; sectionIndex: number }[] {
        const errorLocations: { stepIndex: number; sectionIndex: number }[] = [];
        const erroredFields = new Set<string>();

        // Find all invalid product fields
        Object.keys(productsGroup.controls).forEach(productKey => {
            const productGroup = productsGroup.get(productKey) as FormGroup;
            if (productGroup) {
                Object.keys(productGroup.controls).forEach(fieldName => {
                    const fieldControl = productGroup.get(fieldName);
                    if (fieldControl && fieldControl.invalid && fieldControl.touched) {
                        erroredFields.add(fieldName);
                    }
                });
            }
        });

        // Map each errored field to its step/section location
        erroredFields.forEach(fieldName => {
            const location = this.findFieldLocation(fieldName, config);
            if (location) {
                // Check if this location is not already in the list
                if (
                    !errorLocations.some(
                        loc => loc.stepIndex === location.stepIndex && loc.sectionIndex === location.sectionIndex
                    )
                ) {
                    errorLocations.push(location);
                }
            }
        });

        return errorLocations;
    }

    /**
     * Finds the step and section index where a product-relevant field is defined
     *
     * @param fieldName {string} - The name of the field to find
     * @param config {WorkflowConfig} - The workflow configuration
     * @returns Object with stepIndex and sectionIndex, or null if not found
     */
    private findFieldLocation(
        fieldName: string,
        config: WorkflowConfig
    ): { stepIndex: number; sectionIndex: number } | null {
        if (!config.steps) return null;

        for (let stepIndex = 0; stepIndex < config.steps.length; stepIndex++) {
            const step = config.steps[stepIndex];

            // Check in step fields
            if (step.fields?.some(f => f.name === fieldName && f.productRelevant)) {
                return { stepIndex, sectionIndex: 0 };
            }

            // Check in section fields
            if (step.sections) {
                for (let sectionIndex = 0; sectionIndex < step.sections.length; sectionIndex++) {
                    const section = step.sections[sectionIndex];
                    if (section.fields?.some(f => f.name === fieldName && f.productRelevant)) {
                        return { stepIndex, sectionIndex };
                    }
                }
            }
        }

        return null;
    }

    private hasNestedErrors(control: AbstractControl): boolean {
        if (control instanceof FormGroup) {
            return Object.values(control.controls).some(childControl => {
                if (childControl.errors) {
                    return true;
                }
                if (childControl instanceof FormGroup) {
                    return this.hasNestedErrors(childControl);
                }
                return false;
            });
        }
        return false;
    }

    // Parses backend 400 messages, maps each to its form control via config, marks invalid+touched.
    public applyBackendValidationMessages(messages: string[], config: WorkflowConfig, formGroup: FormGroup): void {
        const labelToName = this.buildJoiLabelToNameMap(config);
        const stepsGroup = formGroup.get('steps') as FormGroup | null;
        const productsGroup = formGroup.get('products') as FormGroup | null;

        for (const message of messages) {
            const fieldName = this.resolveFieldNameFromMessage(message, labelToName);
            if (!fieldName) continue;

            const control =
                this.findControlInSteps(fieldName, stepsGroup) ?? this.findControlInProducts(fieldName, productsGroup);

            if (control) {
                control.setErrors({ ...control.errors, backendError: message });
                control.markAsTouched();
            }
        }
    }

    // Joi uses `productFieldExcelName ?? field.name` as label; build reverse map.
    private buildJoiLabelToNameMap(config: WorkflowConfig): Map<string, string> {
        const map = new Map<string, string>();
        const register = (field: WorkflowConfigField) => map.set(field.productFieldExcelName ?? field.name, field.name);

        config.fields?.forEach(register);
        config.steps?.forEach(step => {
            step.fields?.forEach(register);
            step.sections?.forEach(section => section.fields?.forEach(register));
        });

        return map;
    }

    /** Extracts a field.name from a Joi error message string. Returns null if unparseable. */
    private resolveFieldNameFromMessage(message: string, labelToName: Map<string, string>): string | null {
        // Pattern: The required field 'fieldName' is missing
        const missingMatch = /^The required field '(.+?)' is missing/.exec(message);
        if (missingMatch) return labelToName.get(missingMatch[1]) ?? missingMatch[1];

        // Pattern: The value for 'joiLabel' must be ...  (custom date/pattern messages)
        const valueForMatch = /^The value for '(.+?)'/.exec(message);
        if (valueForMatch) return labelToName.get(valueForMatch[1]) ?? valueForMatch[1];

        // Pattern: "joiLabel" <validation detail>
        const labelMatch = /^"(.+?)"/.exec(message);
        if (labelMatch) return labelToName.get(labelMatch[1]) ?? null;

        return null;
    }

    private findControlInSteps(fieldName: string, stepsGroup: FormGroup | null): AbstractControl | null {
        if (!stepsGroup) return null;

        for (const stepKey of Object.keys(stepsGroup.controls)) {
            const stepGroup = stepsGroup.get(stepKey) as FormGroup;

            // Direct step-level field
            const direct = stepGroup.get(fieldName);
            if (direct) return direct;

            // Section-level field
            const sectionsGroup = stepGroup.get('sections') as FormGroup | null;
            if (sectionsGroup) {
                for (const sectionKey of Object.keys(sectionsGroup.controls)) {
                    const sectionGroup = sectionsGroup.get(sectionKey) as FormGroup;
                    const inSection = sectionGroup.get(fieldName);
                    if (inSection) return inSection;
                }
            }
        }

        return null;
    }

    private findControlInProducts(fieldName: string, productsGroup: FormGroup | null): AbstractControl | null {
        if (!productsGroup) return null;

        for (const productKey of Object.keys(productsGroup.controls)) {
            const productGroup = productsGroup.get(productKey) as FormGroup;
            const inProduct = productGroup?.get(fieldName);
            if (inProduct) return inProduct;
        }

        return null;
    }
}
